import AsyncStorage from "@react-native-async-storage/async-storage";
import NetInfo from "@react-native-community/netinfo";
import { waitForPendingWrites } from "firebase/firestore";

const OFFLINE_WRITE_QUEUE_KEY = "offlineWriteQueueV1";

const safeParseQueue = (raw) => {
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getQueue = async () => {
  const stored = await AsyncStorage.getItem(OFFLINE_WRITE_QUEUE_KEY);
  return safeParseQueue(stored);
};

const setQueue = async (queue) => {
  await AsyncStorage.setItem(OFFLINE_WRITE_QUEUE_KEY, JSON.stringify(queue));
};

export const isDeviceOnline = async () => {
  try {
    const netState = await NetInfo.fetch();
    if (typeof netState?.isInternetReachable === "boolean") {
      return netState.isConnected && netState.isInternetReachable;
    }
    if (typeof netState?.isConnected === "boolean") {
      return netState.isConnected;
    }
  } catch {
    // Fall through to navigator fallback.
  }

  if (typeof navigator !== "undefined" && typeof navigator.onLine === "boolean") {
    return navigator.onLine;
  }

  return true;
};

export const executeFirestoreWriteWithQueue = async ({
  db,
  queueLabel,
  writeOperation,
}) => {
  const wasOnlineAtSubmit = await isDeviceOnline();

  await writeOperation();

  if (!wasOnlineAtSubmit) {
    const queue = await getQueue();
    queue.push({
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      label: queueLabel || "pending-write",
      queuedAt: Date.now(),
    });
    await setQueue(queue);
  }

  return {
    queued: !wasOnlineAtSubmit,
    queueCount: (await getQueue()).length,
  };
};

export const flushPendingFirestoreWrites = async (db) => {
  const online = await isDeviceOnline();
  if (!online) {
    return { flushed: 0, remaining: (await getQueue()).length };
  }

  try {
    await waitForPendingWrites(db);
  } catch {
    // If there are no pending writes, waitForPendingWrites can reject in edge cases.
  }

  const queue = await getQueue();
  const flushedCount = queue.length;
  if (flushedCount > 0) {
    await AsyncStorage.removeItem(OFFLINE_WRITE_QUEUE_KEY);
  }

  return { flushed: flushedCount, remaining: 0 };
};

export const getOfflineQueuedWriteCount = async () => {
  const queue = await getQueue();
  return queue.length;
};
